import React from 'react';
import { useTranslation } from 'react-i18next';
import '../../pages/event/CompanyTournament.css';

/* The competition drawn out — the grid, the bracket, and what one line of the run sheet is called.

   ★★ **Two screens ask the same question and there may only be one answer.** The cockpit asks it
   while the day is being set up ("is my field spread the way I meant it") and the run sheet asks it
   while the day is being written ("what is going on at half two, and where can I put the lunch").
   A second copy of this drawing on the second screen would be a second opinion about one day.

   Everything here is drawn from what the SERVER computed (``preview.grid`` / ``preview.schedule``);
   nothing in this file works out a single minute or pairing of its own.
*/

/* ---- THE FIELD, ROUND BY ROUND ----
   ★★ The run sheet answers "is lunch at a sane hour". This answers the other half of "how will it
   look", and no number in the boxes above shows it: "teams playing at once: 2" reads exactly the
   same whether the day works or not. What an organiser recognises is the GRID — round down the
   side, games across the top, their own team numbers in the cells — because it is the thing they
   will print and hand out.

   ★ The numbers are the real ones. A1..A6, B1.. is the string the schedule generator writes onto a
   team, off the same split, so the picture cannot name a group the team sheets will not.

   ★ Drawn by the SERVER, like the run sheet and for the same reason: a grid built in JavaScript
   would be a second opinion about the same day, and it would disagree with the heat size in the box
   above the first time either changed.

   ⚠️ An example draw, and it says so. Every team is in every game exactly once and the heats are
   the size above — but which numbers actually meet is settled when the schedule is generated, by a
   search that spreads the meetings further than any instant formula can. */
const gridColumnHead = (column, t) => {
  if (column.game && column.court) {
    return (
      <>
        {t('tournamentGridGame', { n: column.game })}
        <span className="ct-plan__sub">{t('tournamentGridCourt', { n: column.court })}</span>
      </>
    );
  }
  if (column.game) return t('tournamentGridGame', { n: column.game });
  if (column.court) return t('tournamentGridCourt', { n: column.court });
  return t('tournamentGridHeat');
};

const bracketSide = (side, t) => {
  if (!side) return '—';
  if (side.team) return side.team;
  if (side.winner) return t('tournamentGridWinnerOf', { n: side.winner });
  if (side.loser) return t('tournamentGridLoserOf', { n: side.loser });
  /* ★ A pools bracket has no numbers in it yet and cannot have: who reaches it is decided by the
     pools that have not been played. "The winner of pool A" is the true and the useful answer, and
     the place is spelt out rather than numbered — "2nd of pool C" is what an organiser says. */
  if (side.pool) return t(`tournamentGridPoolPlace_${side.place > 2 ? 'n' : side.place}`,
    { pool: side.pool, place: side.place });
  return '—';
};

// A bracket's rounds are NAMED, not numbered: "the semi-finals" is what an organiser is looking
// for and "round 4 of 5" is not. Counted from the end, which is where the names come from.
const bracketRoundName = (round, t) => {
  // ★ A round is NAMED only where it has a name. A bracket's last round is the final; a round
  // robin's last round is round eleven and nothing more, and `from_end` comes back null to say so.
  if (round.from_end == null) return t('tournamentGridRound', { n: round.round });
  if (round.from_end === 0) return t('tournamentGridFinal');
  if (round.from_end === 1) return t('tournamentGridSemi');
  if (round.from_end === 2) return t('tournamentGridQuarter');
  return t('tournamentGridRound', { n: round.round });
};

export const PlanGrid = ({ grid }) => {
  const { t } = useTranslation();
  if (!grid) return <p className="ct-field__hint">{t('tournamentGridEmpty')}</p>;

  const match = (m) => (
    <p className="ct-plan__match" key={m.n}>
      <span className="ct-plan__mnum">{m.n}</span>
      <span className="ct-plan__side">{bracketSide(m.a, t)}</span>
      <span className="ct-plan__v">{t('tournamentGridVersus')}</span>
      <span className="ct-plan__side">{bracketSide(m.b, t)}</span>
    </p>
  );

  /* Rounds of matches side by side — a knockout tree, a round robin's fixture list, or the final
     round a set of pools feeds. ★ One function for all three because they are the same picture:
     what differs is only what a SIDE is, and `bracketSide` already answers that for a number, a
     winner-of, a loser-of and a place in a pool. */
  const bracketOf = (b) => (
    <div className="ct-plan__scroll">
      <div className="ct-plan__bracket">
        {b.rounds.map((round) => (
          <div className="ct-plan__round" key={round.round}>
            <p className="ct-plan__roundname">
              {bracketRoundName(round, t)}
              {/* Which time round the field it is, so home-and-away reads as one competition
                  rather than as twenty-two unexplained rounds. */}
              {round.leg ? (
                <span className="ct-plan__sub">{t('tournamentGridLeg', { n: round.leg })}</span>
              ) : null}
            </p>
            {round.matches.map(match)}
            {round.more > 0 && (
              <p className="ct-plan__more">{t('tournamentGridMore', { n: round.more })}</p>
            )}
            {/* The byes are the half of a bracket an organiser did not picture: twelve
                numbers sitting beside four matches is what "12 byes" actually looks like. */}
            {round.byes.length > 0 && (
              <p className="ct-plan__byes">
                {t('tournamentGridByes', {
                  n: round.byes.length,
                  /* ⚠️ Two shapes in flight: a bye used to be a bare number and is becoming a
                     SIDE, the same shape a match has, so that "the winner of pool A" can wait
                     beside the match it is waiting for. Both are read here rather than one,
                     because the screen and shared_models deploy separately — see
                     project_tornea_sm_deploy — and a version gap must not print
                     [object Object] where a team number belongs. */
                  list: round.byes.slice(0, 20)
                    .map((bye) => (typeof bye === 'string' ? bye : bracketSide(bye, t)))
                    .join(', ') + (round.byes.length > 20 ? '…' : ''),
                })}
              </p>
            )}
          </div>
        ))}
        {b.third_place && (
          <div className="ct-plan__round">
            <p className="ct-plan__roundname">{t('tournamentGridThird')}</p>
            {match(b.third_place)}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="ct-plan">
      <p className="ct-plan__title">{t('tournamentGridTitle')}</p>

      {/* ★★ Pools is TWO pictures and they are not the same picture. The pools are a fixture
          list — who plays whom, and there is only one legal answer — while what follows them is a
          bracket whose sides are not numbers yet but places ("the winner of pool A"). Drawing the
          second one with the same function the knockout uses is the whole reason `draw_bracket`
          lives in shared_models rather than inside the knockout: one tree, two fields. */}
      {grid.kind === 'pools' ? (
        <>
          <div className="ct-plan__scroll">
            <div className="ct-plan__bracket">
              {grid.pools.map((pool) => (
                <div className="ct-plan__round" key={pool.letter}>
                  <p className="ct-plan__roundname">
                    {t('tournamentGridPool', { letter: pool.letter, teams: pool.size })}
                  </p>
                  {pool.rounds.map((row) => row.matches.map(match))}
                  {/* An odd pool sits somebody out every round, and that is not a gap in the
                      schedule — it is what an odd pool IS. Saying it here is what stops an
                      organiser counting the matches and concluding one is missing. */}
                  {pool.size % 2 === 1 && (
                    <p className="ct-plan__byes">{t('tournamentGridPoolOdd')}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
          {grid.final && (
            <>
              <p className="ct-plan__roundname mt-3">
                {t('tournamentGridFinalRound', { n: grid.advance })}
              </p>
              {bracketOf(grid.final)}
            </>
          )}
        </>
      ) : grid.kind === 'bracket' || grid.kind === 'fixtures' ? bracketOf(grid) : (
        grid.groups.map((group) => (
          <div className="ct-plan__group" key={group.letter || 'all'}>
            {grid.groups.length > 1 && (
              <p className="ct-plan__roundname">
                {t('tournamentGridGroup', { letter: group.letter, teams: group.size })}
              </p>
            )}
            <div className="ct-plan__scroll">
              <table className="ct-plan__table">
                <thead>
                  <tr>
                    <th>{t('tournamentGridRoundHead')}</th>
                    {grid.columns.map((column, index) => (
                      <th key={`${column.game || 0}-${column.court || 0}-${index}`}>
                        {gridColumnHead(column, t)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.rounds.map((row) => (
                    <tr key={row.round}>
                      <th scope="row">
                        {t('tournamentGridRound', { n: row.round })}
                        {row.game ? (
                          <span className="ct-plan__sub">
                            {t('tournamentGridGame', { n: row.game })}
                          </span>
                        ) : null}
                      </th>
                      {row.heats.map((heat, index) => (
                        <td key={index}>
                          {heat.teams.map((number) => (
                            <span className="ct-plan__num" key={number}>{number}</span>
                          ))}
                          {heat.more > 0 && (
                            <span className="ct-plan__more">
                              {t('tournamentGridMore', { n: heat.more })}
                            </span>
                          )}
                          {/* A court with nobody on it is a real answer — it is what "more
                              courts than the field needs" looks like — not a gap. */}
                          {heat.teams.length === 0 && heat.more === 0 && (
                            <span className="ct-plan__empty">{t('tournamentGridEmptyCourt')}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* A picture that quietly showed four of six rounds would read as a shorter day. */}
      {grid.rounds_shown < grid.rounds_total && (
        <p className="ct-field__hint">
          {t('tournamentGridTrimmed', { shown: grid.rounds_shown, total: grid.rounds_total })}
        </p>
      )}
      <p className="ct-field__hint">
        {/* ★★ A round robin is the ONE grid on the platform that is not a plausible day but THE
            day: there is only one circle, so these pairings are what the generator will write.
            Saying the usual "which numbers actually meet is decided later" over it would be
            false. */}
        {/* ★ Pools sits with the round robin rather than with the rotation: inside a pool there is
            only one circle, so these fixtures ARE the ones that will be played. What is still open
            is which team is A1 — and, for the bracket underneath, who wins the pools. */}
        {t(grid.kind === 'fixtures' ? 'tournamentGridHintRoundRobin'
          : (grid.kind === 'pools' ? 'tournamentGridHintPools'
            : (grid.kind === 'bracket' ? 'tournamentGridHintKnockout' : 'tournamentGridHint')))}
      </p>
    </div>
  );
};


/* What one row of the computed day is CALLED. The server says what happens and for how long; the
   words are here, because "Break" is "the other group is playing" whenever the groups take turns —
   and that is the same rule that greys the break box on the cockpit.

   ★ Here rather than on either screen: the run sheet writes these words down as real slots, so the
   list somebody is looking at and the titles that land on their day have to be the same words. */
export const runLabel = (t, row, breakFollowsGame) => {
  if (row.kind === 'break') {
    return t(breakFollowsGame ? 'tournamentRun_breakAlternate' : 'tournamentRun_break');
  }
  if (row.kind === 'game' || row.kind === 'block') {
    return t(`tournamentRun_${row.kind}`, { number: row.index });
  }
  return t(`tournamentRun_${row.kind}`);
};

export default PlanGrid;
